/**
 * `decrireMath(noeud) → string` — l'expression DITE en français.
 *
 * Une fraction dessinée en SVG est MUETTE : le lecteur d'écran ne voit qu'un
 * chemin et deux textes. Sans cette fonction, un élève non voyant ou
 * dyslexique perdrait exactement ce que le dessin apporte aux autres — et ce
 * n'est pas un détail d'accessibilité, c'est l'ÉNONCÉ.
 *
 * C'est la même règle que `decrireFigureResolue` : la description n'est
 * JAMAIS écrite par un modèle, elle est produite déterministement depuis la
 * structure. Si le modèle l'écrivait, elle pourrait contredire ce qui est
 * dessiné, et l'élève n'aurait aucun moyen de s'en apercevoir.
 *
 * Registre : lue par l'élève, le parent et le tuteur — purement descriptive,
 * ni tutoiement ni vouvoiement.
 */
import { analyserTexteMath } from './analyser.js';
import { sansCible } from './expression.js';
import { formaterMathTexte } from './unicode.js';
/** Les relations et opérateurs, dits en toutes lettres. */
const DIT = {
    '+': 'plus',
    '−': 'moins',
    '-': 'moins',
    '×': 'fois',
    '·': 'fois',
    '÷': 'divisé par',
    /* La composition : « f rond g ». */
    '∘': 'rond',
    '±': 'plus ou moins',
    '=': 'égale',
    '<': 'est plus petit que',
    '>': 'est plus grand que',
    '≤': 'est plus petit ou égal à',
    '≥': 'est plus grand ou égal à',
    '≠': "n'est pas égal à",
    '≈': 'vaut environ',
    '≡': 'est congru à',
    /* Les flèches et les relations d'ensemble : sans elles, un lecteur d'écran
       lisait le GLYPHE (« ∥ », « → »), c'est-à-dire rien du tout. */
    '→': 'flèche vers',
    '⇒': 'donc',
    '⇔': 'équivaut à',
    '↦': 'a pour image',
    '∥': 'est parallèle à',
    '⊥': 'est perpendiculaire à',
    '∈': 'appartient à',
    '∉': "n'appartient pas à",
    '∪': 'union',
    '∩': 'inter',
    /* Les points de suite de `\cdots` : « a fois a et ainsi de suite a ». */
    '···': 'et ainsi de suite',
};
/**
 * Les symboles qui sont des ATOMES et non des opérateurs. Un lecteur d'écran
 * dit mal une lettre grecque isolée — et ne dit rien du tout de `Δ`. Ils sont
 * nommés ici, comme les relations le sont au-dessus.
 */
const SYMBOLE_DIT = {
    π: 'pi',
    '∞': 'infini',
    Δ: 'delta majuscule',
    δ: 'delta',
    α: 'alpha',
    β: 'bêta',
    θ: 'thêta',
};
/** Les exposants et indices Unicode, ramenés à leur valeur pour la lecture. */
const VALEUR_SCRIPT = {
    '⁰': '0',
    '¹': '1',
    '²': '2',
    '³': '3',
    '⁴': '4',
    '⁵': '5',
    '⁶': '6',
    '⁷': '7',
    '⁸': '8',
    '⁹': '9',
    ⁿ: 'n',
    ⁱ: 'i',
    '₀': '0',
    '₁': '1',
    '₂': '2',
    '₃': '3',
    '₄': '4',
    '₅': '5',
    '₆': '6',
    '₇': '7',
    '₈': '8',
    '₉': '9',
    ₙ: 'n',
};
const EXPOSANTS_LUS = '⁰¹²³⁴⁵⁶⁷⁸⁹ⁿⁱ';
/**
 * Les noms d'opérateur (`\sin`, `\log`…, `latex.ts`) dits en toutes lettres
 * (tranche T39). Ce qu'ils prennent se dit « de » : « sinus de 30 degrés »,
 * jamais « sin fois 30° ».
 */
const NOM_DIT = {
    sin: 'sinus',
    cos: 'cosinus',
    tan: 'tangente',
    // Les rapports réciproques (tranche T45) : écrits `sec`, `cosec`, `cotan`.
    sec: 'sécante',
    cosec: 'cosécante',
    csc: 'cosécante',
    cotan: 'cotangente',
    cot: 'cotangente',
    log: 'logarithme',
    ln: 'logarithme naturel',
};
const TRIGONOMETRIQUES = new Set(['sin', 'cos', 'tan']);
/** Le nom DIT d'un opérateur nommé — avec son exposant ou sa base —, `null` sinon. */
function direNomOperateur(noeud) {
    if (noeud.type === 'atome')
        return NOM_DIT[noeud.texte] ?? null;
    if (noeud.type !== 'script' || noeud.base.type !== 'atome')
        return null;
    const nom = NOM_DIT[noeud.base.texte];
    if (nom === undefined)
        return null;
    const morceaux = [nom];
    if (noeud.indice !== undefined)
        morceaux.push(`en base ${decrireMath(noeud.indice)}`);
    if (noeud.exposant !== undefined) {
        const e = noeud.exposant.type === 'atome' ? noeud.exposant.texte : decrireMath(noeud.exposant);
        // `\sin^{-1}` est la fonction réciproque : « arc sinus », pas « sinus exposant −1 ».
        if ((e === '−1' || e === '-1') && TRIGONOMETRIQUES.has(noeud.base.texte))
            return `arc ${nom}`;
        morceaux.push(e === '2' ? 'carré' : e === '3' ? 'cube' : `exposant ${e}`);
    }
    return morceaux.join(' ');
}
/** Ce que prend un nom d'opérateur : `(0,5)` se dit « 0,5 » quand le contenu est court. */
function direArgument(noeud) {
    if (noeud.type === 'groupe' && noeud.delimiteur === 'parentheses' && estCourt(noeud.contenu)) {
        return decrireMath(noeud.contenu);
    }
    return decrireMath(noeud);
}
/** « 30° » se dit « 30 degrés » ; « 1° », « 0,5° » : « degré », au singulier. */
function direDegre(base) {
    const valeur = Number(base.replace(',', '.').replace('−', '-'));
    const singulier = base !== '' && Number.isFinite(valeur) && Math.abs(valeur) < 2;
    return `${direAtome(base)} ${singulier ? 'degré' : 'degrés'}`;
}
/** `3²` se dit « 3 au carré », pas « 3 ² ». Les scripts collés sont dépliés ici. */
function direAtome(texte) {
    const nomme = SYMBOLE_DIT[texte];
    if (nomme !== undefined)
        return nomme;
    if (texte.length > 1 && texte.endsWith('°'))
        return direDegre(texte.slice(0, -1));
    let base = '';
    let script = '';
    for (const c of texte) {
        const valeur = VALEUR_SCRIPT[c];
        if (valeur === undefined)
            base += c;
        else
            script += EXPOSANTS_LUS.includes(c) ? `^${valeur}` : `_${valeur}`;
    }
    if (script === '')
        return base;
    if (script.startsWith('^'))
        return `${base} ${direExposant(script.slice(1))}`;
    return `${base} indice ${script.slice(1)}`;
}
/** « au carré », « au cube », sinon « exposant n ». */
function direExposant(exposant) {
    if (exposant === '2')
        return 'au carré';
    if (exposant === '3')
        return 'au cube';
    return `exposant ${exposant}`;
}
/** Un atome qui n'est QUE de l'espace : il ne se dit pas. */
const estEspaceMath = (n) => n.type === 'atome' && n.texte.length > 0 && /^[\s\u00a0\u202f]+$/.test(n.texte);
/** Une expression assez simple pour être dite sans « la fraction … fin de fraction ». */
function estCourt(noeud) {
    return noeud.type === 'atome' || noeud.type === 'operateur';
}
const OPERATEUR_DE_SYSTEME = ' ; ';
export function decrireMath(noeud) {
    switch (noeud.type) {
        case 'atome':
            return noeud.texte === '?' ? 'un nombre inconnu' : direAtome(noeud.texte);
        case 'operateur':
            return DIT[noeud.texte] ?? noeud.texte;
        case 'rangee': {
            // La multiplication implicite (`4a`, `n(n+1)`) n'a pas de signe à
            // l'écrit : à l'oral, il en faut un, sans quoi « n(n+1) » se lit comme
            // deux choses posées côte à côte.
            const morceaux = [];
            let precedentEstOperande = false;
            let precedentEstNom = false;
            for (const enfant of noeud.enfants) {
                // Une espace (`\quad`, `\ `) est une LARGEUR, pas un terme : la
                // compter comme un opérande faisait dire « 1 fois   fois donc ».
                if (estEspaceMath(enfant))
                    continue;
                // Une cible ne change pas la nature de ce qu'elle marque : `\cible{s}{-}`
                // reste un signe, sinon « moins fois 8 sur 27 ».
                const estOperande = sansCible(enfant).type !== 'operateur';
                const nom = direNomOperateur(enfant);
                if (precedentEstNom && estOperande && nom === null) {
                    // Ce qu'un nom d'opérateur prend : « sinus DE 30 degrés » (tranche T39).
                    morceaux.push('de', direArgument(enfant));
                    precedentEstNom = false;
                }
                else {
                    if (estOperande && precedentEstOperande)
                        morceaux.push(precedentEstNom ? 'de' : 'fois');
                    morceaux.push(nom ?? decrireMath(enfant));
                    // Une matrice suivie d'autre chose dit où elle s'arrête : sans cela,
                    // dans « A B », la seconde se lirait comme une ligne de la première.
                    if (sansCible(enfant).type === 'matrice' && enfant !== dernierDit(noeud.enfants)) {
                        morceaux.push(`${morceaux.pop() ?? ''}, fin de la matrice`);
                    }
                    precedentEstNom = nom !== null;
                }
                precedentEstOperande = estOperande;
            }
            return morceaux.join(' ');
        }
        case 'fraction': {
            const n = decrireMath(noeud.numerateur);
            const d = decrireMath(noeud.denominateur);
            // Une fraction simple se dit d'un souffle ; une fraction composée a
            // besoin qu'on dise où elle s'arrête, sinon la phrase devient ambiguë.
            if (estCourt(noeud.numerateur) && estCourt(noeud.denominateur)) {
                return `${n} sur ${d}`;
            }
            return `la fraction ${n} sur ${d}, fin de la fraction`;
        }
        case 'script': {
            const base = decrireMath(noeud.base);
            const morceaux = [base];
            if (noeud.indice !== undefined)
                morceaux.push(`indice ${decrireMath(noeud.indice)}`);
            if (noeud.exposant !== undefined) {
                const litteral = noeud.exposant.type === 'atome' ? noeud.exposant.texte : decrireMath(noeud.exposant);
                morceaux.push(direExposant(litteral));
            }
            return morceaux.join(' ');
        }
        case 'racine': {
            const contenu = decrireMath(noeud.radicande);
            if (noeud.degre === undefined) {
                return estCourt(noeud.radicande)
                    ? `racine carrée de ${contenu}`
                    : `racine carrée de ${contenu}, fin de la racine`;
            }
            const degre = decrireMath(noeud.degre);
            const nom = degre === '2' ? 'carrée' : degre === '3' ? 'cubique' : `${degre}-ième`;
            return `racine ${nom} de ${contenu}, fin de la racine`;
        }
        case 'valeurAbsolue':
            return `valeur absolue de ${decrireMath(noeud.contenu)}, fin de la valeur absolue`;
        /* La norme d'un vecteur se DIT comme en classe : « norme du vecteur u ».
           Sur autre chose qu'un vecteur nommé, la fin se dit, comme la valeur
           absolue (tranche T43). */
        case 'norme': {
            const contenu = sansCible(noeud.contenu);
            if (contenu.type === 'surmonte' && contenu.ornement === 'fleche') {
                return `norme du vecteur ${decrireMath(contenu.contenu)}`;
            }
            return `norme de ${decrireMath(noeud.contenu)}, fin de la norme`;
        }
        /* Un trait au-dessus de deux lettres se DIT, il ne se décrit pas : en
           classe on lit « segment AB », jamais « A B surmonté d'une barre ». */
        case 'surmonte': {
            const contenu = decrireMath(noeud.contenu);
            return noeud.ornement === 'barre' ? `segment ${contenu}` : `vecteur ${contenu}`;
        }
        case 'groupe': {
            const contenu = decrireMath(noeud.contenu);
            if (noeud.delimiteur === 'crochets')
                return `crochet ouvrant ${contenu} crochet fermant`;
            if (noeud.delimiteur === 'accolades')
                return `accolade ouvrante ${contenu} accolade fermante`;
            return `parenthèse ouvrante ${contenu} parenthèse fermante`;
        }
        case 'systeme': {
            const lignes = noeud.lignes.map(decrireMath).join(OPERATEUR_DE_SYSTEME);
            return `système de ${noeud.lignes.length} équations : ${lignes}`;
        }
        case 'grandOperateur': {
            const nom = noeud.operateur === 'somme'
                ? 'somme'
                : noeud.operateur === 'produit'
                    ? 'produit'
                    : 'intégrale';
            const bornes = [];
            if (noeud.borneInf !== undefined)
                bornes.push(`de ${decrireMath(noeud.borneInf)}`);
            if (noeud.borneSup !== undefined)
                bornes.push(`à ${decrireMath(noeud.borneSup)}`);
            const entete = bornes.length === 0 ? nom : `${nom} ${bornes.join(' ')}`;
            return `${entete} de ${decrireMath(noeud.corps)}, fin de la ${nom}`;
        }
        /* La marque ne se dit pas : c'est le trait du prof qui la montre, et
           l'annotation, lue à la suite de la ligne, qui en parle. */
        case 'cible':
            return decrireMath(noeud.contenu);
        case 'sousAccolade':
            return `${decrireMath(noeud.contenu)}, soit ${decrireMath(noeud.legende)}`;
        /* « matrice de deux lignes et deux colonnes : 0, −1 ; 1, 0 » — les cases
           d'une ligne séparées par des virgules, les lignes par des points-virgules,
           comme on la dicte (tranche T41). */
        case 'matrice': {
            const lignes = noeud.lignes.length;
            const colonnes = noeud.lignes[0]?.length ?? 0;
            const cases = noeud.lignes.map((ligne) => ligne.map(decrireMath).join(', ')).join(' ; ');
            return `matrice ${lignes === 1 ? 'd’' : 'de '}${compter(lignes, 'ligne')} et ${compter(colonnes, 'colonne')} : ${cases}`;
        }
    }
}
const NOMBRES_DITS = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit'];
/** « une ligne », « deux colonnes » : les dimensions d'une matrice se disent en lettres. */
function compter(n, nom) {
    return `${NOMBRES_DITS[n] ?? String(n)} ${nom}${n > 1 ? 's' : ''}`;
}
/** Le dernier enfant qui se DIT : une espace finale ne compte pas. */
function dernierDit(enfants) {
    for (let i = enfants.length - 1; i >= 0; i -= 1) {
        const e = enfants[i];
        if (e !== undefined && !estEspaceMath(e))
            return e;
    }
    return undefined;
}
/**
 * La phrase ANNONCÉE pour un texte entier : la prose telle quelle, et chaque
 * expression DITE. Une seule source pour l'étiquette d'une ligne — donc
 * jamais de divergence entre ce qui est dessiné et ce qui est lu.
 *
 * La ponctuation est resserrée après coup : insérer « 3 sur 4 » au milieu
 * d'une phrase laisse un blanc devant la virgule qui suit, et un lecteur
 * d'écran marque ce blanc. La règle suivie est celle du français — pas
 * d'espace devant `.` `,` `)`, une espace devant `?` `!` `:` `;`.
 */
export function direTexteMath(texte) {
    return analyserTexteMath(texte)
        .map((segment) => segment.type === 'prose'
        ? formaterMathTexte(segment.texte)
        : ` ${decrireMath(segment.noeud)} `)
        .join('')
        .replace(/ {2,}/g, ' ')
        .replace(/ +([.,)\]])/g, '$1')
        .trim();
}
//# sourceMappingURL=decrire.js.map